import { useCallback } from 'react'
import { useToastStore, type ToastVariant, type ToastAction } from './toastStore'

const DEFAULT_DURATION_MS = 2000
const LONG_MESSAGE_CHARS = 30
const LONG_MESSAGE_DURATION_MS = 3000

const getDurationMs = (message: string): number =>
  message.length > LONG_MESSAGE_CHARS ? LONG_MESSAGE_DURATION_MS : DEFAULT_DURATION_MS

export const useToast = () => {
  const add = useToastStore((s) => s.add)

  const toast = useCallback(
    (
      message: string,
      options?: {
        variant?: ToastVariant
        durationMs?: number
        action?: ToastAction
      },
    ) => {
      return add(message, {
        ...options,
        durationMs: options?.durationMs ?? getDurationMs(message),
      })
    },
    [add],
  )

  const success = useCallback(
    (message: string, opts?: { durationMs?: number; action?: ToastAction }) =>
      toast(message, { ...opts, variant: 'success' }),
    [toast],
  )

  const info = useCallback(
    (message: string, opts?: { durationMs?: number; action?: ToastAction }) =>
      toast(message, { ...opts, variant: 'info' }),
    [toast],
  )

  const error = useCallback(
    (message: string, opts?: { durationMs?: number; action?: ToastAction }) =>
      toast(message, { ...opts, variant: 'error' }),
    [toast],
  )

  const warning = useCallback(
    (message: string, opts?: { durationMs?: number; action?: ToastAction }) =>
      toast(message, { ...opts, variant: 'warning' }),
    [toast],
  )

  const generic = useCallback(
    (message: string, opts?: { durationMs?: number; action?: ToastAction }) =>
      toast(message, { ...opts, variant: 'generic' }),
    [toast],
  )

  /** Notificação push estruturada: título + corpo, 5s ou até fechar. */
  const pushNotification = useCallback(
    (title: string, body?: string, opts?: { action?: ToastAction }) => {
      return add(body ?? '', {
        variant: 'info',
        title,
        durationMs: 5000,
        ...opts,
      })
    },
    [add],
  )

  return { toast, success, info, error, warning, generic, pushNotification }
}
